// Funções de persistência do tenant DSS, espelhando o contrato de storage
// do TenantService Angular (salvarTenantStorage / salvarTenantSchemaStorage).
// O formato Base64 aqui deve ser idêntico ao que o Angular produz, pois o
// Angular leaf que monta depois lê com atob() via getTenantStorage().
import { TENANT_KEY, TENANT_SCHEMA_KEY } from '../http/auth-storage';
import { decodeUser } from '../layout/session/session-decode';
import { isIntegratorOrDistributor } from '../layout/session/user-type';

// Mesma chave de app/const.ts (NOME_CONTA_KEY). Declarada aqui, e não importada de
// layout/session/tenant-storage, para manter este módulo sem dependência do fluxo de
// troca de conta da navbar — que notifica listeners (notifyTenantChanged) irrelevantes
// para o handshake DSS e assimétricos em relação ao saveTenantStorage local.
const ACCOUNT_NAME_KEY = 'nome_conta';

// Espelha toBinaryStr() do Angular (app/commons/toBinaryStr.ts): substitui aspas
// curvas por aspas retas antes do btoa, tornando o JSON seguro para Latin-1.
function toBinaryStr(value: string): string {
  return value.replaceAll('\u2019', "'");
}

/**
 * Persiste o schema temporário enquanto o objeto Tenant completo ainda não foi
 * buscado, espelhando tenantService.salvarTenantSchemaStorage(). O axios-instance
 * React lê essa chave para montar o header `tenant` durante a requisição.
 */
export function saveTenantSchemaStorage(schema: string): void {
  sessionStorage.setItem(TENANT_SCHEMA_KEY, schema);
}

/**
 * Remove o tenant já persistido (e o nome da conta associado), espelhando
 * tenantService.limparTenantStorage(). Obrigatório ANTES de gravar tenant_schema:
 * getTenantHeader() (http/auth-storage) resolve o header por XOR e devolve null
 * quando `tenant` e `tenant_schema` coexistem. Numa reentrada no iframe o
 * sessionStorage sobrevive com o `tenant` da visita anterior, então gravar o schema
 * sem limpar deixaria as duas chaves presentes, a requisição sairia sem header e o
 * handshake abortaria sem enviar 'loaded' — spinner infinito no Sigma Cloud.
 */
export function clearTenantStorage(): void {
  sessionStorage.removeItem(TENANT_KEY);
  sessionStorage.removeItem(ACCOUNT_NAME_KEY);
}

/** Remove o schema temporário após o objeto completo ter sido gravado. */
export function clearTenantSchemaStorage(): void {
  sessionStorage.removeItem(TENANT_SCHEMA_KEY);
}

/**
 * Persiste o objeto Tenant completo codificado em Base64, espelhando
 * tenantService.salvarTenantStorage(). O Angular leaf lê essa chave com atob()
 * em getTenantStorage(); a codificação precisa ser exatamente a mesma.
 */
export function saveTenantStorage(tenant: unknown): void {
  const encoded = btoa(toBinaryStr(JSON.stringify(tenant)));
  sessionStorage.setItem(TENANT_KEY, encoded);
}

// Mesma chave de auth.service.ts (USUARIO_KEY). Declarada aqui, junto das demais do
// handshake, para manter o contrato de storage com o Angular explícito num só lugar.
const USER_KEY = 'user_instance';

/**
 * Persiste o usuário logado codificado em Base64, espelhando
 * authService.saveUsuarioStorage(). O Angular leaf lê essa chave com atob() em
 * getUsuarioStorage(), e telas de feature acessam propriedades do retorno sem checar
 * null — a ausência da chave derruba o ngOnInit delas.
 */
export function saveUserStorage(user: unknown): void {
  sessionStorage.setItem(USER_KEY, btoa(toBinaryStr(JSON.stringify(user))));
}

/**
 * Remove o usuário persistido. Usado quando a busca falha, para o handshake não
 * deixar meia sessão gravada (tenant sem usuário) que a próxima montagem herdaria.
 */
export function clearUserStorage(): void {
  sessionStorage.removeItem(USER_KEY);
}

// Credenciais que o handshake grava antes de qualquer requisição. Moram aqui, e não no
// hook, porque a limpeza abaixo é quem as remove — chave e remoção no mesmo lugar.
export const DSS_TOKEN_STORAGE_KEY = 'token';
// Minúscula de propósito: é a constante IID de auth.service.ts, lida pelo Angular em
// UsuarioService.getUsuario(), TenantService e list-conta. sessionStorage diferencia
// caixa, então gravar 'IID' deixaria todos esses consumidores com null.
export const DSS_IID_STORAGE_KEY = 'iid';

function hasStoredValue(key: string): boolean {
  const value = sessionStorage.getItem(key);
  return value !== null && value !== '';
}

/**
 * Indica se o sessionStorage já guarda uma sessão utilizável, como a deixada por um login
 * normal na mesma aba. Token e usuário são sempre exigidos: a folha lê o usuário sem
 * checar null, e meia sessão derrubaria o ngOnInit das telas. O tenant é exigido só de
 * quem não é integrador nem distribuidor.
 */
export function hasStoredSession(): boolean {
  if (!hasStoredValue(DSS_TOKEN_STORAGE_KEY) || !hasStoredValue(USER_KEY)) {
    return false;
  }
  // Integrador e distribuidor sem conta selecionada são estados legítimos: telas
  // pré-tenant como list-conta apagam o tenant no próprio ngOnInit, e o distribuidor
  // (sem contas) nem chega a ter um — a home dele é list-integrador. Exigi-lo deixaria a
  // rota DSS aguardando um handshake que, fora do iframe do Sigma, nunca chega.
  return (
    hasStoredValue(TENANT_KEY) ||
    isIntegratorOrDistributor(decodeUser(sessionStorage.getItem(USER_KEY)))
  );
}

/**
 * Remove as credenciais gravadas no início do handshake. Obrigatório em todo aborto:
 * token e iid são gravados antes das requisições de tenant e usuário, então limpar
 * apenas tenant e usuário ainda deixaria a próxima montagem herdando meia sessão —
 * credencial presente, sessão utilizável ausente.
 */
export function clearHandshakeCredentialsStorage(): void {
  sessionStorage.removeItem(DSS_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(DSS_IID_STORAGE_KEY);
}
